import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ActiveTrial {
  id: string;
  title: string;
  nct_id?: string;
  sponsor_name?: string;
  sponsor_organization_id?: string | null;
  sponsor_contact_email?: string;
  sponsor_contact_name?: string;
  eu_ct_number?: string;
  ind_number?: string;
  intervention_code?: string;
  phase?: string;
  status: string;
  target_enrollment?: number;
  protocol_version?: string;
  start_date?: string;
  expected_completion_date?: string;
  contract_value?: number;
  created_at: string;
  updated_at: string;
  cro_organization_id: string;
  cro_trial_sites?: {
    site_organization_id: string;
    enrollment_target?: number;
  }[];
}

export interface PatientTrialStatus {
  id: string;
  patient_id: string;
  trial_id?: string;
  trial_site_id?: string; // Nullable for sponsor-direct patients
  sponsor_organization_id?: string; // Set for sponsor-direct patients
  status: string;
  status_date?: string;
  reason?: string;
  created_at: string;
  updated_at: string;
  patient?: {
    id: string;
    full_name?: string;
    patient_number?: string;
  };
}

export function useActiveTrials(siteOrgId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['active-trials', siteOrgId],
    queryFn: async () => {
      if (!siteOrgId) return [];

      const { data, error } = await supabase
        .from('cro_trial_sites')
        .select(`
          *,
          trial:cro_trials!inner(*)
        `)
        .eq('site_organization_id', siteOrgId);
      
      if (error) {
        logger.error('Error fetching active trials:', error);
        throw error;
      }

      return (data || []).map((item: any) => ({
        ...item.trial,
        cro_trial_sites: [{ 
          site_organization_id: item.site_organization_id,
          enrollment_target: item.enrollment_target 
        }]
      })) as ActiveTrial[];
    },
    enabled: !!siteOrgId,
    staleTime: 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (newTrial: {
      trial: Omit<ActiveTrial, 'id' | 'created_at' | 'updated_at' | 'cro_trial_sites' | 'cro_organization_id'>;
      siteOrgId: string;
      croOrgId?: string | null;
      enrollmentTarget?: number;
    }) => {
      // Resolve sponsor organization: use passed id, or upsert by name
      let sponsorOrgId: string | null = newTrial.trial.sponsor_organization_id ?? null;
      if (!sponsorOrgId && newTrial.trial.sponsor_name?.trim()) {
        const sponsorName = newTrial.trial.sponsor_name.trim();
        const { data: existing } = await supabase
          .from('organizations')
          .select('id')
          .eq('type', 'sponsor')
          .ilike('name', sponsorName)
          .maybeSingle();
        if (existing?.id) {
          sponsorOrgId = existing.id;
        } else {
          const { data: created, error: createErr } = await supabase
            .from('organizations')
            .insert({ name: sponsorName, type: 'sponsor' } as any)
            .select('id')
            .single();
          if (createErr) {
            logger.warn('Sponsor org upsert failed (non-blocking):', createErr);
          } else {
            sponsorOrgId = created.id;
          }
        }
      }

      // A trial is always owned by a sponsor. If no CRO is provided, the site brought it directly.
      // cro_organization_id is NOT NULL in schema — keep it as siteOrgId only when no CRO is known,
      // but sponsor_organization_id is the true owner and used by all display layers going forward.
      const croOrgId = newTrial.croOrgId || newTrial.siteOrgId;

      const normalizedTrialStatus = newTrial.trial.status === 'active' ? 'enrolling' : newTrial.trial.status;

      const { data: trialData, error: trialError } = await supabase
        .from('cro_trials')
        .insert({
          title: newTrial.trial.title,
          nct_id: newTrial.trial.nct_id,
          phase: newTrial.trial.phase,
          status: normalizedTrialStatus,
          target_enrollment: newTrial.trial.target_enrollment,
          protocol_version: newTrial.trial.protocol_version,
          start_date: newTrial.trial.start_date,
          expected_completion_date: newTrial.trial.expected_completion_date,
          contract_value: newTrial.trial.contract_value,
          cro_organization_id: croOrgId,
          sponsor_organization_id: sponsorOrgId,
        } as any)
        .select()
        .single();

      if (trialError) throw trialError;

      const { error: linkError } = await supabase
        .from('cro_trial_sites')
        .insert({
          cro_trial_id: trialData.id,
          site_organization_id: newTrial.siteOrgId,
          site_name: 'Trial Site',
          site_status: 'activated',
          enrollment_target: newTrial.enrollmentTarget,
        });

      if (linkError) throw linkError;

      return { trialData, siteOrgId: newTrial.siteOrgId, sponsorOrgId };
    },
    onSuccess: async (result) => {
      // Auto-seed TMF requirements for the linked site
      // Look up the trial_sites linked to this org to seed their TMF
      if (result.siteOrgId) {
        try {
          const { data: sites } = await supabase
            .from('trial_sites_safe')
            .select('id')
            .eq('organization_id', result.siteOrgId)
            .eq('status', 'active')
            .limit(1);

          if (sites && sites.length > 0) {
            for (const site of sites) {
              try {
                await supabase.rpc('seed_default_tmf_requirements', { p_site_id: site.id });
              } catch (seedError) {
                logger.error(`TMF auto-seed warning for site ${site.id}:`, seedError);
              }
            }
          }
        } catch (err) {
          logger.error('TMF auto-seed lookup warning (non-blocking):', err);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['active-trials'] });
      toast({
        title: "Success",
        description: "Active trial added successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add active trial",
        variant: "destructive",
      });
    },
  });

  return {
    ...query,
    createActiveTrial: createMutation.mutate,
    isCreating: createMutation.isPending,
  };
}

export function useTrialPatients(trialId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['trial-patients', trialId],
    queryFn: async () => {
      if (!trialId) return [];

      const { data, error } = await supabase
        .from('patient_trial_status')
        .select('*')
        .eq('trial_id', trialId)
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error('Error fetching trial patients:', error);
        throw error;
      }

      return (data || []).map(item => ({
        ...item,
        patient: { id: item.patient_id, patient_number: item.patient_id.substring(0, 8) }
      })) as PatientTrialStatus[];
    },
    enabled: !!trialId,
    staleTime: 30 * 1000,
  });

  const assignPatientMutation = useMutation({
    mutationFn: async (assignment: {
      patient_id: string;
      trial_id: string;
      trial_site_id: string;
      status: string;
      skipVisitGeneration?: boolean;
    }) => {
      const { skipVisitGeneration, ...row } = assignment;
      const { data, error } = await supabase
        .from('patient_trial_status')
        .insert(row)
        .select()
        .single();

      if (error) throw error;
      
      // Auto-generate patient visits based on trial protocol
      // (skipped when the enroll step already scheduled them from the chosen baseline)
      if (!skipVisitGeneration) try {
        const { error: visitError } = await supabase.functions.invoke('generate-patient-visits', {
          body: {
            patientId: assignment.patient_id,
            trialId: assignment.trial_id,
            trialSiteId: assignment.trial_site_id,
            enrollmentDate: new Date().toISOString(),
          }
        });
        if (visitError) logger.error('Error generating visits:', visitError);
      } catch (e) {
        logger.error('Failed to generate visits:', e);
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['trial-patients'] });
      queryClient.invalidateQueries({ queryKey: ['patient-visits'] });
      toast({
        title: "Success",
        description: "Patient assigned to trial and visits scheduled",
      });

      // Audit trail: patient assigned to trial
      supabase.auth.getUser().then(({ data: authData }) => {
        if (authData?.user?.id) {
          supabase.from('audit_trails').insert({
            user_id: authData.user.id,
            action: 'patient_assigned_to_trial',
            entity_type: 'patient_trial_status',
            entity_id: variables.trial_site_id,
            details: {
              patient_id: variables.patient_id,
              trial_id: variables.trial_id,
              trial_site_id: variables.trial_site_id,
              initial_status: variables.status,
              assigned_at: new Date().toISOString(),
            },
            risk_level: 'high',
            compliance_frameworks: ['FDA_21_CFR_11', 'GCP_ICH'],
            reason: `Patient assigned to trial`,
          }).then(({ error: auditErr }) => {
            if (auditErr) logger.warn('Audit trail insert failed:', auditErr);
          });
        }
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to assign patient",
        variant: "destructive",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updates: any = { 
        status,
        status_date: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('patient_trial_status')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['trial-patients'] });
      toast({
        title: "Success",
        description: "Patient status updated",
      });

      // Audit trail: patient status changed
      supabase.auth.getUser().then(({ data: authData }) => {
        if (authData?.user?.id) {
          supabase.from('audit_trails').insert({
            user_id: authData.user.id,
            action: 'patient_status_changed',
            entity_type: 'patient_trial_status',
            entity_id: variables.id,
            details: {
              new_status: variables.status,
              status_date: data?.status_date,
              patient_id: data?.patient_id,
              trial_id: data?.trial_id,
            },
            risk_level: variables.status === 'withdrawn' || variables.status === 'screen_failed' ? 'high' : 'medium',
            compliance_frameworks: ['FDA_21_CFR_11', 'GCP_ICH'],
            reason: `Patient status changed to ${variables.status}`,
          }).then(({ error: auditErr }) => {
            if (auditErr) logger.warn('Audit trail insert failed:', auditErr);
          });
        }
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    },
  });

  return {
    ...query,
    assignPatient: assignPatientMutation.mutate,
    updatePatientStatus: updateStatusMutation.mutate,
    isAssigning: assignPatientMutation.isPending,
    isUpdating: updateStatusMutation.isPending,
  };
}
